import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { usePasswordVisibility } from '../../hooks/usePasswordVisibility';
import { useAuth } from '../../hooks/useAuth';
import { en } from '../../translations/en';
import { logger } from '../../utils/logger';

export const PasswordResetSection = () => {
  const { changeUserPassword, isLoading, error: authError } = useAuth();

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const currentPassword = usePasswordVisibility();
  const newPassword = usePasswordVisibility();
  const confirmPassword = usePasswordVisibility();

  const handlePasswordChange =
    (field: keyof typeof passwordForm) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordForm((prev) => ({ ...prev, [field]: event.target.value }));
      setPasswordError(null);
      setPasswordSuccess(false);
    };

  const handleSubmit = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);

    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    try {
      await changeUserPassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordSuccess(true);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (err: unknown) {
      const error = err as Error;
      logger.error('Password change failed', err);
      setPasswordError(error.message || 'Password change failed');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
      {passwordSuccess && (
        <Typography color="success.main" sx={{ fontSize: '0.875rem' }}>
          Password changed successfully!
        </Typography>
      )}
      {(passwordError || authError) && (
        <Typography color="error.main" sx={{ fontSize: '0.875rem' }}>
          {passwordError || authError}
        </Typography>
      )}
      <TextField
        type={currentPassword.inputType}
        label={en.settings.account.passwordReset.current}
        value={passwordForm.currentPassword}
        onChange={handlePasswordChange('currentPassword')}
        fullWidth
        variant="outlined"
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                aria-label="toggle current password visibility"
                onClick={currentPassword.toggleVisibility}
                edge="end"
                disabled={isLoading}
              >
                {currentPassword.showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        type={newPassword.inputType}
        label={en.settings.account.passwordReset.new}
        value={passwordForm.newPassword}
        onChange={handlePasswordChange('newPassword')}
        fullWidth
        variant="outlined"
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                aria-label="toggle new password visibility"
                onClick={newPassword.toggleVisibility}
                edge="end"
                disabled={isLoading}
              >
                {newPassword.showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        type={confirmPassword.inputType}
        label={en.settings.account.passwordReset.confirm}
        value={passwordForm.confirmPassword}
        onChange={handlePasswordChange('confirmPassword')}
        fullWidth
        variant="outlined"
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                aria-label="toggle confirm password visibility"
                onClick={confirmPassword.toggleVisibility}
                edge="end"
                disabled={isLoading}
              >
                {confirmPassword.showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={
          isLoading ||
          !passwordForm.currentPassword ||
          !passwordForm.newPassword ||
          !passwordForm.confirmPassword
        }
        startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
      >
        {isLoading ? 'Updating...' : en.settings.account.passwordReset.button}
      </Button>
    </Box>
  );
};
